import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Sparkles, Paperclip, Bot, User, ArrowRight, Minus, Download, Plus, Mic, Trash2, FileText, FileSpreadsheet, Image, Archive } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Returns an icon based on file extension
const FileIcon = ({ name }) => {
    const ext = name.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return <FileText className="w-3 h-3 text-red-400 flex-shrink-0" />;
    if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet className="w-3 h-3 text-emerald-400 flex-shrink-0" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="w-3 h-3 text-purple-400 flex-shrink-0" />;
    if (['zip'].includes(ext)) return <Archive className="w-3 h-3 text-yellow-400 flex-shrink-0" />;
    return <Paperclip className="w-3 h-3 text-blue-400 flex-shrink-0" />;
};

const AiUploadModal = ({ onDataExtracted, formType }) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [files, setFiles] = useState([]);
    const [isMinimized, setIsMinimized] = useState(true);
    const [isListening, setIsListening] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const chatEndRef = useRef(null);
    const textareaRef = useRef(null);
    const recognitionRef = useRef(null);
    const dragCounterRef = useRef(0);

    useEffect(() => {
        const handleOpen = () => setIsMinimized(false);
        window.addEventListener('openAiChat', handleOpen);
        return () => window.removeEventListener('openAiChat', handleOpen);
    }, []);

    useEffect(() => {
        if (!isMinimized) {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isMinimized]);

    useEffect(() => {
        const body = document.body;
        if (!isMinimized) {
            body.style.transition = 'padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
            body.style.paddingRight = '380px';
        } else {
            body.style.paddingRight = '0px';
        }
        return () => { body.style.paddingRight = '0px'; };
    }, [isMinimized]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [inputValue, isMinimized]);

    const addFiles = useCallback((incoming) => {
        const newFiles = Array.from(incoming);
        setFiles(prev => {
            const existing = new Set(prev.map(f => `${f.name}_${f.size}`));
            const unique = newFiles.filter(f => !existing.has(`${f.name}_${f.size}`));
            return [...prev, ...unique];
        });
    }, []);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounterRef.current = 0;
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    }, [addFiles]);

    const startListening = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.warn("Speech recognition is not supported in this browser.");
            return;
        }
        try {
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';
            recognition.onstart = () => { setIsListening(true); toast.info("Listening... Speak now"); };
            recognition.onerror = () => setIsListening(false);
            recognition.onend = () => setIsListening(false);
            recognition.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                setInputValue(prev => prev ? prev + ' ' + transcript : transcript);
            };
            recognitionRef.current = recognition;
            recognition.start();
        } catch (e) {
            setIsListening(false);
        }
    };

    const stopListening = () => {
        recognitionRef.current?.stop();
        setIsListening(false);
    };

    const toggleListening = () => isListening ? stopListening() : startListening();

    const parseImportData = (text) => {
        const matches = [...text.matchAll(/\[IMPORT_DATA\]([\s\S]*?)\[\/IMPORT_DATA\]/g)];
        if (matches.length === 0) return null;
        let merged = null;
        for (const match of matches) {
            try {
                const parsed = JSON.parse(match[1]);
                if (!merged) {
                    merged = parsed;
                } else {
                    const mergeObjects = (target, source) => {
                        Object.keys(source).forEach(key => {
                            if (Array.isArray(source[key])) {
                                target[key] = Array.isArray(target[key]) ? [...target[key], ...source[key]] : [...source[key]];
                            } else if (source[key] && typeof source[key] === 'object') {
                                target[key] = target[key] || {};
                                mergeObjects(target[key], source[key]);
                            } else {
                                target[key] = source[key];
                            }
                        });
                    };
                    mergeObjects(merged, parsed);
                }
            } catch (e) {
                console.error("Failed to parse IMPORT_DATA block", e);
            }
        }
        return merged;
    };

    const handleSend = async (e) => {
        e?.preventDefault();
        if (!inputValue.trim() && files.length === 0) return;

        const fileNames = files.map(f => f.name).join(', ');
        const userMsgText = inputValue || (files.length > 0 ? `Analyze: ${fileNames}` : 'Document uploaded');
        setMessages(prev => [...prev, { role: 'user', parts: [{ text: userMsgText }] }]);
        const currentInput = inputValue;
        const currentFiles = [...files];
        setInputValue('');
        setFiles([]);
        setIsTyping(true);

        const formData = new FormData();
        formData.append('message', currentInput || `Analyze these files`);
        formData.append('history', JSON.stringify(messages));
        formData.append('formType', formType);
        currentFiles.forEach(f => formData.append('files', f));

        try {
            const response = await axios.post(`${API_BASE_URL}/api/ai/chat`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response.data.success) {
                setMessages(prev => [...prev, { role: 'model', parts: [{ text: response.data.reply }] }]);
            } else {
                throw new Error(response.data.error || 'Server error');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            toast.error(`Chat Error: ${errorMsg}`);
            setMessages(prev => [...prev, { role: 'model', parts: [{ text: `Error: ${errorMsg}` }] }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleApplyData = (data) => {
        if (data) {
            window.dispatchEvent(new CustomEvent('aiDataExtracted', { detail: data }));
            if (onDataExtracted) {
                onDataExtracted(data);
            }
            toast.success("Data applied to form!");
        }
    };

    const downloadJson = (data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ai_extracted_${data.formType || 'data'}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.info("Debug JSON downloaded");
    };

    const handleClearChat = () => {
        setMessages([]);
        setFiles([]);
        setInputValue('');
        toast.info("Conversation cleared");
    };

    const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

    const canSend = inputValue.trim() || files.length > 0;

    return (
        <>
            <AnimatePresence>
                {!isMinimized && (
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 26, stiffness: 220 }}
                        className="fixed top-0 right-0 h-screen w-[380px] bg-[#161618] border-l border-neutral-800 flex flex-col z-[9999] shadow-2xl pointer-events-auto"
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <AnimatePresence>
                            {isDragging && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-blue-950/80 backdrop-blur-sm border-2 border-dashed border-blue-400/60 rounded-none pointer-events-none"
                                >
                                    <motion.div
                                        animate={{ scale: [1, 1.08, 1] }}
                                        transition={{ repeat: Infinity, duration: 1.2 }}
                                        className="bg-blue-600/20 p-5 rounded-full border border-blue-500/40 mb-4"
                                    >
                                        <Archive className="w-10 h-10 text-blue-400" />
                                    </motion.div>
                                    <p className="text-sm font-bold text-blue-300">Drop files here</p>
                                    <p className="text-[10px] text-blue-400/70 mt-1">PDF, Excel, Image, ZIP supported</p>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="bg-neutral-900/90 px-5 py-4 flex justify-between items-center text-white shrink-0 border-b border-neutral-800">
                            <div className="flex items-center gap-2.5">
                                <div className="bg-blue-600/20 p-1.5 rounded-xl border border-blue-500/20">
                                    <Sparkles className="w-4 h-4 text-blue-400" />
                                </div>
                                <div>
                                    <h2 className="text-xs font-bold tracking-wide text-neutral-100">Neural Assistant</h2>
                                    <div className="flex items-center gap-1.5">
                                        <span className="relative flex h-1.5 w-1.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                                        </span>
                                        <span className="text-[9px] text-neutral-400 font-semibold uppercase tracking-wider">Online</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button type="button" onClick={handleClearChat} title="Clear chat"
                                    className="hover:bg-neutral-800 p-2 rounded-xl transition-all text-neutral-400 hover:text-red-400 active:scale-90">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => setIsMinimized(true)} title="Minimize"
                                    className="hover:bg-neutral-800 p-2 rounded-xl transition-all text-neutral-400 hover:text-white active:scale-90">
                                    <Minus className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => setIsMinimized(true)} title="Close"
                                    className="hover:bg-neutral-800 p-2 rounded-xl transition-all text-neutral-400 hover:text-white active:scale-90">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {messages.length === 0 && !isTyping ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-4 bg-gradient-to-b from-neutral-950/40 to-neutral-900/10">
                                <div className="bg-neutral-800/50 p-4 rounded-full border border-neutral-700/50 text-blue-400">
                                    <Bot className="w-8 h-8" />
                                </div>
                                <div className="space-y-1.5">
                                    <h3 className="text-sm font-bold text-neutral-200">Welcome to TaxEases AI</h3>
                                    <p className="text-xs text-neutral-400 max-w-[240px] leading-relaxed mx-auto">
                                        Ask tax questions or drop return documents (GSTR 1, GSTR 7, TDS) to auto-fill pages.
                                    </p>
                                </div>
                                <div className="mt-2 border border-dashed border-neutral-700 rounded-xl px-4 py-3 text-[10px] text-neutral-500 flex flex-col items-center gap-1">
                                    <Archive className="w-4 h-4 text-neutral-600 mb-0.5" />
                                    <span>Drag & drop files here, or use the <strong className="text-neutral-400">＋</strong> button</span>
                                    <span className="text-neutral-600">PDF · Excel · Image · ZIP</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gradient-to-b from-neutral-950/40 to-neutral-900/10 scrollbar-thin scrollbar-thumb-neutral-800 scrollbar-track-transparent">
                                {messages.map((msg, idx) => {
                                    const importData = msg.role === 'model' ? parseImportData(msg.parts[0].text) : null;
                                    const displayText = msg.role === 'model'
                                        ? msg.parts[0].text.replace(/\[IMPORT_DATA\][\s\S]*?\[\/IMPORT_DATA\]/g, '').trim()
                                        : msg.parts[0].text;

                                    return (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            key={idx}
                                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div className={`flex gap-3 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                                <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-neutral-800 text-blue-400 border border-neutral-700'}`}>
                                                    {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                                                </div>
                                                <div className="space-y-2">
                                                    <div className={`px-4 py-2.5 rounded-[18px] text-xs leading-relaxed border ${
                                                        msg.role === 'user'
                                                            ? 'bg-blue-600/20 border-blue-500/30 rounded-tr-none'
                                                            : 'bg-neutral-900/60 border-neutral-800/80 rounded-tl-none'
                                                    }`}>
                                                        <div className={`whitespace-pre-wrap font-semibold ${msg.role === 'user' ? '!text-white' : '!text-neutral-200'}`}>{displayText}</div>
                                                    </div>

                                                    {importData && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            className="bg-emerald-950/20 backdrop-blur-sm border border-emerald-500/20 rounded-[18px] p-3.5 space-y-2.5 shadow-lg relative overflow-hidden group"
                                                        >
                                                            <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-[10px] uppercase tracking-wider">
                                                                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                                                                Data Payload Detected
                                                            </div>
                                                            <p className="text-[10px] text-neutral-400 font-medium">We found structured {importData.formType} data. Ready to import?</p>
                                                            <div className="flex gap-2">
                                                                <button type="button" onClick={() => handleApplyData(importData)}
                                                                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-xl font-bold text-[10px] transition-all flex items-center justify-center gap-1.5 active:scale-95 shadow-md shadow-emerald-900/30 uppercase tracking-widest">
                                                                    Apply to Form <ArrowRight className="w-3.5 h-3.5" />
                                                                </button>
                                                                <button type="button" onClick={() => downloadJson(importData)} title="Download extracted JSON"
                                                                    className="w-10 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-xl flex items-center justify-center transition-all active:scale-90 border border-neutral-700">
                                                                    <Download className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                                {isTyping && (
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                                        <div className="w-7 h-7 rounded-xl bg-neutral-800 text-blue-400 border border-neutral-700 flex items-center justify-center shadow-md">
                                            <Bot className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="bg-neutral-900/60 p-3 rounded-[18px] rounded-tl-none border border-neutral-800 flex items-center gap-2.5">
                                            <div className="flex gap-1">
                                                <span className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></span>
                                                <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"></span>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                <div ref={chatEndRef} />
                            </div>
                        )}

                        <form onSubmit={handleSend} className="w-full bg-[#1e1e20] border-t border-neutral-800 flex flex-col p-4 shrink-0">
                            <textarea
                                ref={textareaRef}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend(e);
                                    }
                                }}
                                rows={1}
                                placeholder="Upload any document for GSTR 1, GSTR 7 or TDS, I will help prepare your return — or ask anything"
                                className="w-full bg-transparent text-white placeholder-neutral-500 text-xs focus:outline-none resize-none px-2 py-1.5 leading-relaxed min-h-[40px] max-h-[120px] scrollbar-none"
                            />

                            <AnimatePresence>
                                {files.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="flex flex-wrap gap-1.5 mx-1 mb-2 mt-1"
                                    >
                                        {files.map((f, i) => (
                                            <motion.div
                                                key={`${f.name}_${i}`}
                                                className="flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 rounded-lg px-2 py-1 text-[10px] text-neutral-300 font-medium max-w-[160px]"
                                            >
                                                <FileIcon name={f.name} />
                                                <span className="truncate">{f.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(i)}
                                                    className="ml-0.5 hover:bg-neutral-600 rounded p-0.5 transition-colors"
                                                >
                                                    <X className="w-2.5 h-2.5 text-neutral-400 hover:text-white" />
                                                </button>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="flex justify-between items-center px-1 pt-2">
                                <div className="flex items-center gap-2">
                                    <label
                                        className="cursor-pointer bg-neutral-800/50 hover:bg-neutral-800 p-1.5 rounded-full transition-colors flex items-center justify-center active:scale-95 border border-neutral-700/50"
                                    >
                                        <input
                                            type="file"
                                            className="hidden"
                                            multiple
                                            onChange={(e) => addFiles(e.target.files)}
                                        />
                                        <Plus className={`w-3.5 h-3.5 text-white`} />
                                    </label>
                                    {files.length > 0 && (
                                        <span className="text-[9px] font-bold text-white bg-blue-600/30 border border-blue-400/40 rounded-full px-1.5 py-0.5">
                                            {files.length} file{files.length > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={toggleListening}
                                        className={`p-1.5 rounded-full transition-colors ${isListening ? 'bg-red-600/20 text-red-400 border border-red-500/30' : 'bg-neutral-800/50 text-neutral-300 border border-neutral-700/50'}`}
                                    >
                                        <Mic className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isTyping || !canSend}
                                        className={`w-7 h-7 rounded-full transition-all duration-300 flex items-center justify-center ${
                                            !canSend || isTyping
                                                ? 'bg-neutral-800 text-neutral-600 cursor-not-allowed'
                                                : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20 active:scale-90'
                                        }`}
                                    >
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {isMinimized && (
                <motion.button
                    drag
                    dragMomentum={false}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsMinimized(false)}
                    className="fixed bottom-6 right-8 z-[9999] pointer-events-auto cursor-move bg-[#1e1e20] text-white px-5 py-4 rounded-full shadow-[0_12px_40px_rgba(0,0,0,0.3)] flex items-center gap-3.5 border border-neutral-800 hover:border-neutral-700 transition-all select-none group"
                >
                    <div className="bg-blue-600 p-2 rounded-xl shadow-md shadow-blue-500/20 group-hover:rotate-12 transition-transform pointer-events-none">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left pointer-events-none">
                        <span className="block text-[9px] font-black uppercase tracking-[0.15em] text-blue-400 leading-none mb-0.5">Ask AI</span>
                        <span className="block text-xs font-bold leading-none text-neutral-200">Neural Assistant</span>
                    </div>
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse pointer-events-none"></div>
                </motion.button>
            )}
        </>
    );
};

export default AiUploadModal;
